import React, { FC, useEffect, useState } from "react"
import SubmitButton from "@bancacfplus/SubmitButton"
import Checkbox from "@bancacfplus/Checkbox"
import { useMutation } from "@apollo/client/react"
import { useForm } from "react-hook-form"
import { ErrorBoundaryEnriched } from "../ErrorBoundaryEnriched"
import { CompleteConsentsMainText } from "./CompleteConsentsMainText"
import { editGdprPreferencesMutation } from "./mutations/editGdprPreferences"
import { setGdprPreferencesMutation } from "./mutations/setGdprPreferences"
import {
  editGdprPreferences,
  editGdprPreferencesVariables
} from "./mutations/__generated-typings__/editGdprPreferences"
import { setGdprPreferences, setGdprPreferencesVariables } from "./mutations/__generated-typings__/setGdprPreferences"
import { Consents } from "./enums/consents"
import style from "./CompleteConsents.module.scss"
import { getDocsByClientSource } from "../../utils/getDocsByClientSource"

type ConsentsValues =
  | Consents.SERVICES
  | Consents.MARKETING
  | Consents.PROFILING
  | Consents.THIRD_PARTIES
  | Consents.WHATSAPP
  | Consents.TERMS_AND_CONDITIONS

export type FormValues = { [consent in ConsentsValues]: boolean }

const mainInput = {
  name: Consents.SERVICES,
  text: (
    <div>
      Ho preso visione dell&apos;
      <a
        className={style.link}
        target="_blank"
        href={getDocsByClientSource().informativaPrivacy}
        rel="noopener noreferrer"
      >
        Informativa
      </a>{" "}
      ed acconsento al trattamento dei miei dati personali
    </div>
  )
}

const inputs = [
  {
    name: Consents.MARKETING,
    text: "Promozione e marketing"
  },
  {
    name: Consents.PROFILING,
    text: "Attività di profilazione"
  },
  {
    name: Consents.THIRD_PARTIES,
    text: "Comunicazione dei miei dati personali a partner commerciali di Banca CF+"
  }
]

const termsAndConditions = {
  name: Consents.TERMS_AND_CONDITIONS
}

type CompleteConsentsProps = {
  email: string
  closeConsents: () => void
  collapsed: boolean
  title: string
  submitLabel: string
  consents: FormValues | undefined
}

export const CompleteConsents: FC<CompleteConsentsProps> = ({
  email,
  closeConsents,
  collapsed,
  title,
  submitLabel,
  consents
}) => {
  const [hidden, setHidden] = useState<boolean>(collapsed)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm<FormValues>({ defaultValues: consents })
  const watchServices = watch(mainInput.name)

  const [
    setGdprPreferencesM,
    { data: dataSetGdprPreferences, loading: loadingSetGdprPreferences, error: errorSetGdprPreferences }
  ] = useMutation<setGdprPreferences, setGdprPreferencesVariables>(setGdprPreferencesMutation)

  const [
    editGdprPreferencesM,
    { data: dataEditGdprPreferences, loading: loadingEditGdprPreferences, error: errorEditGdprPreferences }
  ] = useMutation<editGdprPreferences, editGdprPreferencesVariables>(editGdprPreferencesMutation)

  useEffect(() => {
    if (dataSetGdprPreferences && !errorSetGdprPreferences) {
      closeConsents()
    }
  }, [closeConsents, dataSetGdprPreferences, errorSetGdprPreferences])

  useEffect(() => {
    if (dataEditGdprPreferences && !errorEditGdprPreferences) {
      closeConsents()
    }
  }, [closeConsents, dataEditGdprPreferences, errorEditGdprPreferences])

  const onSubmit = (data: FormValues) => {
    if (consents) {
      editGdprPreferencesM({
        variables: { email, source: "UPDATE_PLATFORM", ...data }
      })
    } else {
      setGdprPreferencesM({
        variables: { email, source: "PLATFORM", ...data }
      })
    }
  }

  useEffect(() => {
    if (hidden) {
      inputs.forEach(input => {
        setValue(input.name, watchServices)
      })
    }
  }, [hidden, watchServices, setValue])

  const servicesRegister = register(mainInput.name, { required: true })
  const termsRegister = register(termsAndConditions.name, { required: true })

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)}>
      <h1 className={style.modalTitle}>{title}</h1>

      {(errorSetGdprPreferences || errorEditGdprPreferences) && (
        <ErrorBoundaryEnriched error={errorSetGdprPreferences || errorEditGdprPreferences} errorContainer />
      )}

      <div className={style.form}>
        <Checkbox
          propsRef={servicesRegister.ref as unknown as React.RefObject<HTMLInputElement>}
          input={servicesRegister}
          name={mainInput.name}
          text={hidden ? <CompleteConsentsMainText setHidden={setHidden} /> : mainInput.text}
          error="Questo campo è obbligatorio"
          invalid={!!errors[mainInput.name]}
        />

        <div className={hidden ? style.hidden : ""}>
          <p className={style.privacyText}>Acconsento al trattamento dei dati per le finalità di:</p>
          {inputs.map(field => {
            const fieldRegister = register(field.name)
            return (
              <Checkbox
                key={field.name}
                propsRef={fieldRegister.ref as unknown as React.RefObject<HTMLInputElement>}
                input={fieldRegister}
                name={field.name}
                text={field.text}
              />
            )
          })}
        </div>

        <Checkbox
          propsRef={termsRegister.ref as unknown as React.RefObject<HTMLInputElement>}
          input={termsRegister}
          name={termsAndConditions.name}
          text={
            <p className={style.text}>
              Accetto
              <a target="_blank" href={getDocsByClientSource().termsAndConditions} rel="noopener noreferrer">
                termini e condizioni
              </a>
              per l&apos;utilizzo della piattaforma Banca CF+
            </p>
          }
          error="Questo campo è obbligatorio"
          invalid={!!errors[termsAndConditions.name]}
        />
      </div>

      <SubmitButton loading={loadingSetGdprPreferences || loadingEditGdprPreferences} type="submit">
        {submitLabel}
      </SubmitButton>
    </form>
  )
}
